'use client';

import { resolveEnhancerBrowserUploadAccept } from '@/ai/image/upload-image-preprocess-policy';
import { useWorkspacePaidAccessState } from '@/cms/components/blocks/workspace/workspace-1/actions/use-workspace-paid-access-state';
import type {
  WorkspaceToolParamView,
  WorkspaceToolPrefillFieldView,
} from '@/cms/components/blocks/workspace/workspace-1/contracts/tool-view';
import { calculateWorkspacePrefillRequiredCredits } from '@/cms/components/blocks/workspace/workspace-1/prefill/workspace-prefill-application';
import type { WorkspacePrefillFormModel } from '@/cms/components/blocks/workspace/workspace-1/prefill/workspace-prefill-model';
import { WorkspaceMobileDrawer } from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-mobile-drawer';
import { WorkspaceParamControls } from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-param-controls';
import { useWorkspaceIsMobileViewport } from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-viewport';
import type {
  WorkspacePrefillRequestV1,
  WorkspacePrefillValue,
} from '@/cms/components/blocks/workspace/workspace-prefill-contract';
import { useCmsComponentButton } from '@/cms/components/component-button-action';
import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { Button } from '@/components/ui/actions/button';
import { useAuthSessionObserver } from '@/lib/auth/auth-session-observer';
import {
  type CSSProperties,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

type Hero2ControlsProps = {
  prefillForm: WorkspacePrefillFormModel;
  premiumFeatureTooltip: string;
  promptPlaceholder: string;
  primaryButtonLabel: string;
};

type ValueChangeHandler = (
  fieldId: string,
  value: WorkspacePrefillValue | undefined
) => void;

const HERO_2_REFERENCE_QUEUE_MOTION = {
  durationMs: 420,
  staggerMs: 70,
} as const;

function getReferenceQueueMotionTotalMs(lastStaggerStep: number) {
  return (
    HERO_2_REFERENCE_QUEUE_MOTION.durationMs +
    Math.max(0, lastStaggerStep) * HERO_2_REFERENCE_QUEUE_MOTION.staggerMs
  );
}

function getDisplayValue(
  field: { defaultValue?: unknown },
  value: WorkspacePrefillValue | undefined
) {
  const resolved = value ?? field.defaultValue;
  return isWorkspacePrefillValue(resolved) ? resolved : undefined;
}

function isWorkspacePrefillValue(
  value: unknown
): value is WorkspacePrefillValue {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    (Array.isArray(value) &&
      value.every(
        (item) =>
          typeof item === 'string' ||
          typeof item === 'number' ||
          typeof item === 'boolean'
      ))
  );
}

function isSelectedOption(
  value: WorkspacePrefillValue | undefined,
  optionValue: unknown
) {
  return Array.isArray(value)
    ? value.some((item) => Object.is(item, optionValue))
    : Object.is(value, optionValue);
}

function getParameterSummaryLabel(
  field: WorkspaceToolParamView,
  value: WorkspacePrefillValue | undefined
) {
  if (
    field.type === 'select' ||
    field.type === 'segmented-slider' ||
    field.type === 'multi-select'
  ) {
    const labels = (field.options ?? [])
      .filter((option) => isSelectedOption(value, option.value))
      .map((option) => option.label);
    return labels.join(', ') || field.label;
  }
  if (field.type === 'switch') {
    return field.label;
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? String(value.length) : field.label;
  }
  return value === undefined || value === '' ? field.label : String(value);
}

function getReferenceFileKey(file: File) {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

function Hero2ReferenceField({
  drawerId,
  field,
  files,
  inputId,
  isMobileViewport,
  onFilesChange,
}: {
  drawerId: string;
  field: WorkspaceToolPrefillFieldView;
  files: File[];
  inputId: string;
  isMobileViewport: boolean;
  onFilesChange: (fieldId: string, files: File[]) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isReferenceDialogOpen, setIsReferenceDialogOpen] = useState(false);
  const [isQueueSettled, setIsQueueSettled] = useState(false);
  const [isCollapsedQueueSettled, setIsCollapsedQueueSettled] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const uploadPickerActiveRef = useRef(false);
  const preserveExpandedRef = useRef(false);
  const stackTriggerRef = useRef<HTMLButtonElement | null>(null);
  const queueItemRefs = useRef(new Map<string, HTMLElement>());
  const previousQueuePositionsRef = useRef(new Map<string, DOMRect>());
  const queueAnimationStartIndexRef = useRef(0);
  const enteringFileKeysRef = useRef(new Set<string>());
  const entryOriginRectRef = useRef<DOMRect | null>(null);
  const maxCount = field.imageConstraints?.maxCount ?? 1;
  const canAddMore = files.length < maxCount;

  useLayoutEffect(() => {
    if (typeof URL.createObjectURL !== 'function') {
      setPreviewUrls([]);
      return;
    }
    const nextPreviewUrls = files.map((file) => URL.createObjectURL(file));
    setPreviewUrls(nextPreviewUrls);
    return () => {
      for (const previewUrl of nextPreviewUrls) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [files]);

  useEffect(() => {
    if (!isExpanded || files.length === 0) {
      setIsQueueSettled(false);
      return;
    }
    setIsQueueSettled(false);
    const queueItemCount = files.length + (canAddMore ? 1 : 0);
    const settleTimer = window.setTimeout(
      () => setIsQueueSettled(true),
      getReferenceQueueMotionTotalMs(queueItemCount - 1)
    );
    return () => window.clearTimeout(settleTimer);
  }, [canAddMore, files.length, isExpanded]);

  useEffect(() => {
    if (files.length === 0 || isExpanded) {
      setIsCollapsedQueueSettled(false);
      return;
    }
    if (isMobileViewport) {
      setIsCollapsedQueueSettled(true);
      return;
    }
    setIsCollapsedQueueSettled(false);
    const settleTimer = window.setTimeout(
      () => setIsCollapsedQueueSettled(true),
      getReferenceQueueMotionTotalMs(files.length)
    );
    return () => window.clearTimeout(settleTimer);
  }, [files.length, isExpanded, isMobileViewport]);

  useEffect(() => {
    if (!isMobileViewport) {
      setIsReferenceDialogOpen(false);
    }
  }, [isMobileViewport]);

  useEffect(() => {
    const input = fileInputRef.current;
    if (!input) {
      return;
    }
    function handleCancel() {
      uploadPickerActiveRef.current = false;
      preserveExpandedRef.current = true;
      setIsExpanded(!isMobileViewport);
    }
    input.addEventListener('cancel', handleCancel);
    return () => input.removeEventListener('cancel', handleCancel);
  }, [isMobileViewport]);

  useLayoutEffect(() => {
    if (isMobileViewport) {
      previousQueuePositionsRef.current = new Map();
      enteringFileKeysRef.current = new Set();
      entryOriginRectRef.current = null;
      return;
    }
    const previousPositions = previousQueuePositionsRef.current;
    if (
      previousPositions.size === 0 &&
      enteringFileKeysRef.current.size === 0
    ) {
      return;
    }

    const queueKeys = files.map(getReferenceFileKey);
    if (canAddMore) {
      queueKeys.push('__add__');
    }
    const startIndex = queueAnimationStartIndexRef.current;
    for (const [index, key] of queueKeys.entries()) {
      const node = queueItemRefs.current.get(key);
      const previousRect =
        previousPositions.get(key) ??
        (enteringFileKeysRef.current.has(key)
          ? entryOriginRectRef.current
          : null);
      if (!node || !previousRect || typeof node.animate !== 'function') {
        continue;
      }
      const nextRect = node.getBoundingClientRect();
      const offsetX = previousRect.left - nextRect.left;
      const offsetY = previousRect.top - nextRect.top;
      if (Math.abs(offsetX) < 0.5 && Math.abs(offsetY) < 0.5) {
        continue;
      }
      node.animate(
        [{ translate: `${offsetX}px ${offsetY}px` }, { translate: '0 0' }],
        {
          duration: HERO_2_REFERENCE_QUEUE_MOTION.durationMs,
          delay:
            Math.max(0, index - startIndex) *
            HERO_2_REFERENCE_QUEUE_MOTION.staggerMs,
          easing: 'ease',
        }
      );
    }

    previousQueuePositionsRef.current = new Map();
    enteringFileKeysRef.current = new Set();
    entryOriginRectRef.current = null;
  }, [canAddMore, files, isMobileViewport]);

  function captureQueuePositions() {
    return new Map(
      Array.from(queueItemRefs.current.entries(), ([key, node]) => [
        key,
        node.getBoundingClientRect(),
      ])
    );
  }

  function addFiles(nextFiles: File[]) {
    const merged = [...files];
    const appendedFiles: File[] = [];
    for (const nextFile of nextFiles) {
      const duplicate = merged.some(
        (file) =>
          file.name === nextFile.name &&
          file.size === nextFile.size &&
          file.lastModified === nextFile.lastModified
      );
      if (!duplicate && merged.length < maxCount) {
        merged.push(nextFile);
        appendedFiles.push(nextFile);
      }
    }
    if (appendedFiles.length === 0) {
      return;
    }
    setIsQueueSettled(false);
    if (isMobileViewport) {
      onFilesChange(field.id, merged);
      return;
    }
    previousQueuePositionsRef.current = captureQueuePositions();
    queueAnimationStartIndexRef.current = files.length;
    enteringFileKeysRef.current = new Set(
      appendedFiles.map(getReferenceFileKey)
    );
    entryOriginRectRef.current =
      queueItemRefs.current.get('__add__')?.getBoundingClientRect() ??
      stackTriggerRef.current?.getBoundingClientRect() ??
      null;
    onFilesChange(field.id, merged);
  }

  function beginFileSelection() {
    setIsQueueSettled(false);
    uploadPickerActiveRef.current = true;
    setIsExpanded(!isMobileViewport);
  }

  function finishFileSelection() {
    uploadPickerActiveRef.current = false;
    preserveExpandedRef.current = true;
    setIsExpanded(!isMobileViewport);
  }

  function removeFile(index: number) {
    if (isMobileViewport) {
      const nextFiles = files.filter((_, fileIndex) => fileIndex !== index);
      onFilesChange(field.id, nextFiles);
      if (nextFiles.length === 0) {
        setIsReferenceDialogOpen(false);
      }
      return;
    }
    setIsQueueSettled(false);
    preserveExpandedRef.current = true;
    setIsExpanded(true);
    previousQueuePositionsRef.current = captureQueuePositions();
    queueAnimationStartIndexRef.current = index;
    enteringFileKeysRef.current = new Set();
    entryOriginRectRef.current = null;
    onFilesChange(
      field.id,
      files.filter((_, fileIndex) => fileIndex !== index)
    );
  }

  return (
    <div
      className="cms-block-hero-hero-2-hero-2__referenceField"
      data-expanded={isExpanded}
      data-has-files={files.length > 0}
      data-queue-settled={isQueueSettled}
      data-collapsed-queue-settled={isMobileViewport || isCollapsedQueueSettled}
      style={
        {
          '--hero-2-reference-motion-duration': `${HERO_2_REFERENCE_QUEUE_MOTION.durationMs}ms`,
        } as CSSProperties
      }
      onMouseEnter={() => {
        if (!isMobileViewport && !isExpanded) {
          setIsQueueSettled(false);
          setIsExpanded(true);
        }
      }}
      onMouseLeave={() => {
        if (
          !isMobileViewport &&
          !uploadPickerActiveRef.current &&
          !preserveExpandedRef.current
        ) {
          setIsQueueSettled(false);
          setIsExpanded(false);
        }
      }}
      onPointerMove={() => {
        if (!isMobileViewport && !uploadPickerActiveRef.current) {
          preserveExpandedRef.current = false;
        }
      }}
      onFocusCapture={() => {
        if (!isMobileViewport && !isExpanded) {
          setIsQueueSettled(false);
          setIsExpanded(true);
        }
      }}
      onBlurCapture={(event) => {
        if (
          !isMobileViewport &&
          event.relatedTarget !== null &&
          !event.currentTarget.contains(event.relatedTarget)
        ) {
          setIsQueueSettled(false);
          setIsExpanded(false);
        }
      }}
    >
      <button
        ref={stackTriggerRef}
        type="button"
        className="cms-block-hero-hero-2-hero-2__referenceStackTrigger cms-block-hero-hero-2-hero-2__referenceTile"
        data-has-preview={files.length > 0}
        data-hero-2-reference-trigger={field.id}
        aria-label={field.label}
        aria-expanded={isMobileViewport ? isReferenceDialogOpen : isExpanded}
        aria-haspopup={
          isMobileViewport && files.length > 0 ? 'dialog' : undefined
        }
        onClick={() => {
          if (files.length === 0) {
            beginFileSelection();
            document.getElementById(inputId)?.click();
            return;
          }
          if (isMobileViewport) {
            setIsExpanded(false);
            setIsQueueSettled(false);
            setIsReferenceDialogOpen(true);
            return;
          }
          setIsQueueSettled(false);
          setIsExpanded((current) => !current);
        }}
      >
        {files.length === 0
          ? renderAppIcon({
              name: 'Plus',
              className: 'cms-block-hero-hero-2-hero-2__referenceTileIcon',
            })
          : null}
        {files.length > 0 ? (
          <span
            className="cms-block-hero-hero-2-hero-2__referenceCount"
            aria-hidden="true"
          >
            {renderAppIcon({
              name: 'Plus',
              className: 'cms-block-hero-hero-2-hero-2__referenceCountIcon',
            })}
          </span>
        ) : null}
      </button>

      {files.length > 0 ? (
        <div className="cms-block-hero-hero-2-hero-2__referenceTray">
          {files.map((file, index) => (
            <div
              key={getReferenceFileKey(file)}
              className="cms-block-hero-hero-2-hero-2__referenceTrayItem"
              ref={(node) => {
                const key = getReferenceFileKey(file);
                if (node) {
                  queueItemRefs.current.set(key, node);
                } else {
                  queueItemRefs.current.delete(key);
                }
              }}
              style={
                {
                  '--hero-2-reference-index': index,
                  '--hero-2-reference-expand-delay': `${
                    (files.length - 1 - index) *
                    HERO_2_REFERENCE_QUEUE_MOTION.staggerMs
                  }ms`,
                  '--hero-2-reference-collapse-delay': `${
                    (index + 1) * HERO_2_REFERENCE_QUEUE_MOTION.staggerMs
                  }ms`,
                } as CSSProperties
              }
            >
              {previewUrls[index] ? (
                <img
                  src={previewUrls[index]}
                  alt=""
                  className="cms-block-hero-hero-2-hero-2__referencePreview"
                />
              ) : (
                renderAppIcon({
                  name: 'Image',
                  className: 'cms-block-hero-hero-2-hero-2__referenceTileIcon',
                })
              )}
              <button
                type="button"
                className="cms-block-hero-hero-2-hero-2__referenceRemove"
                aria-label={`Remove ${file.name}`}
                onClick={() => removeFile(index)}
              >
                {renderAppIcon({
                  name: 'X',
                  className:
                    'cms-block-hero-hero-2-hero-2__referenceRemoveIcon',
                })}
              </button>
            </div>
          ))}

          {canAddMore ? (
            <label
              ref={(node) => {
                if (node) {
                  queueItemRefs.current.set('__add__', node);
                } else {
                  queueItemRefs.current.delete('__add__');
                }
              }}
              htmlFor={inputId}
              className="cms-block-hero-hero-2-hero-2__referenceAddTile"
              onClick={beginFileSelection}
              style={
                {
                  '--hero-2-reference-index': files.length,
                  '--hero-2-reference-expand-delay': `${
                    files.length * HERO_2_REFERENCE_QUEUE_MOTION.staggerMs
                  }ms`,
                  '--hero-2-reference-collapse-delay': '0ms',
                } as CSSProperties
              }
            >
              {renderAppIcon({
                name: 'Plus',
                className: 'cms-block-hero-hero-2-hero-2__referenceTileIcon',
              })}
            </label>
          ) : null}
        </div>
      ) : null}

      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        className="sr-only"
        data-hero-2-field-id={field.id}
        accept={resolveEnhancerBrowserUploadAccept(
          field.imageConstraints?.allowedMimeTypes
        )}
        multiple={maxCount > 1}
        aria-label={`Add ${field.label}`}
        onChange={(event) => {
          addFiles(Array.from(event.currentTarget.files ?? []));
          event.currentTarget.value = '';
          finishFileSelection();
        }}
      />

      {isMobileViewport ? (
        <WorkspaceMobileDrawer
          id={drawerId}
          open={isReferenceDialogOpen}
          onOpenChange={setIsReferenceDialogOpen}
          title={field.label}
          size="content"
          bodyClassName="cms-block-hero-hero-2-hero-2__referenceDrawerBody"
        >
          <div className="cms-block-hero-hero-2-hero-2__referenceDrawerGrid">
            {files.map((file, index) => (
              <div
                key={getReferenceFileKey(file)}
                className="cms-block-hero-hero-2-hero-2__referenceDrawerItem"
              >
                {previewUrls[index] ? (
                  <img
                    src={previewUrls[index]}
                    alt=""
                    className="cms-block-hero-hero-2-hero-2__referencePreview"
                  />
                ) : (
                  renderAppIcon({
                    name: 'Image',
                    className:
                      'cms-block-hero-hero-2-hero-2__referenceTileIcon',
                  })
                )}
                <button
                  type="button"
                  className="cms-block-hero-hero-2-hero-2__referenceRemove"
                  aria-label={`Remove ${file.name}`}
                  onClick={() => removeFile(index)}
                >
                  {renderAppIcon({
                    name: 'X',
                    className:
                      'cms-block-hero-hero-2-hero-2__referenceRemoveIcon',
                  })}
                </button>
              </div>
            ))}
            {canAddMore ? (
              <label
                htmlFor={inputId}
                className="cms-block-hero-hero-2-hero-2__referenceAddTile"
                aria-label={`Add ${field.label}`}
                onClick={beginFileSelection}
              >
                {renderAppIcon({
                  name: 'Plus',
                  className: 'cms-block-hero-hero-2-hero-2__referenceTileIcon',
                })}
              </label>
            ) : null}
          </div>
        </WorkspaceMobileDrawer>
      ) : null}
    </div>
  );
}

export function Hero2ControlsClient({
  prefillForm,
  premiumFeatureTooltip,
  promptPlaceholder,
  primaryButtonLabel,
}: Hero2ControlsProps) {
  const formId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const authSession = useAuthSessionObserver();
  const { showPaidFeatureMarkers } = useWorkspacePaidAccessState(
    authSession.currentUserId
  );
  const isMobileViewport = useWorkspaceIsMobileViewport();
  const buttonBinding = useCmsComponentButton('workspaceLaunch');
  const [openSurface, setOpenSurface] = useState<'model' | 'parameters' | null>(
    null
  );
  const [valuesByFieldId, setValuesByFieldId] = useState<
    Record<string, WorkspacePrefillValue>
  >({});
  const [filesByFieldId, setFilesByFieldId] = useState<Record<string, File[]>>(
    {}
  );
  const promptField = prefillForm.fields.find(
    (field) => field.inputRole === 'prompt' && field.control === 'text'
  );
  const imageFields = prefillForm.fields.filter(
    (field) => field.control === 'image_file'
  );
  const parameterFields = prefillForm.parameterFields;
  const parameterValuesByFieldId = Object.fromEntries(
    parameterFields.flatMap((field) => {
      const value = getDisplayValue(field, valuesByFieldId[field.id]);
      return value === undefined ? [] : [[field.id, value]];
    })
  );
  const parameterSummaryItems = parameterFields
    .filter((field) => field.type !== 'switch')
    .map((field) => ({
      id: field.id,
      label: getParameterSummaryLabel(
        field,
        parameterValuesByFieldId[field.id]
      ),
    }));
  const requiredCredits = calculateWorkspacePrefillRequiredCredits({
    form: prefillForm,
    request: {
      version: 1,
      valuesByFieldId,
      filesByFieldId,
    },
  });

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpenSurface(null);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  function handleValueChange(
    fieldId: string,
    value: WorkspacePrefillValue | undefined
  ) {
    setValuesByFieldId((current) => {
      const next = { ...current };
      if (value === undefined) {
        delete next[fieldId];
      } else {
        next[fieldId] = value;
      }
      return next;
    });
  }

  function handleFilesChange(fieldId: string, files: File[]) {
    setFilesByFieldId((current) => {
      const next = { ...current };
      if (files.length === 0) {
        delete next[fieldId];
      } else {
        next[fieldId] = files;
      }
      return next;
    });
  }

  function handleSubmit() {
    const request: WorkspacePrefillRequestV1 = {
      version: 1,
      valuesByFieldId,
      filesByFieldId,
    };
    if (!buttonBinding.managed || !buttonBinding.button) {
      throw new Error(
        `Hero-2 is missing its required Workspace action for ${prefillForm.toolLabel}.`
      );
    }

    buttonBinding.button.trigger({ workspacePrefill: request });
  }

  return (
    <div ref={rootRef} className="cms-block-hero-hero-2-hero-2__panel">
      <div className="cms-block-hero-hero-2-hero-2__shell">
        <div className="cms-block-hero-hero-2-hero-2__promptPanel">
          <div className="cms-block-hero-hero-2-hero-2__promptBody">
            {imageFields.length > 0 ? (
              <div className="cms-block-hero-hero-2-hero-2__referenceTiles">
                {imageFields.map((field) => {
                  const inputId = `${formId}-${field.id}`;
                  return (
                    <Hero2ReferenceField
                      drawerId={`workspace-hero-2-${prefillForm.toolId}-${field.id}-reference-drawer`}
                      key={field.id}
                      field={field}
                      files={filesByFieldId[field.id] ?? []}
                      inputId={inputId}
                      isMobileViewport={isMobileViewport}
                      onFilesChange={handleFilesChange}
                    />
                  );
                })}
              </div>
            ) : null}

            <div className="cms-block-hero-hero-2-hero-2__promptInputWrap">
              {promptField ? (
                <>
                  <label
                    htmlFor={`${formId}-${promptField.id}`}
                    className="sr-only"
                  >
                    {promptField.label}
                  </label>
                  <textarea
                    id={`${formId}-${promptField.id}`}
                    className="cms-block-hero-hero-2-hero-2__promptInput"
                    data-hero-2-field-id={promptField.id}
                    aria-describedby={
                      promptField.infoText
                        ? `${formId}-${promptField.id}-hint`
                        : undefined
                    }
                    placeholder={promptPlaceholder}
                    value={
                      typeof getDisplayValue(
                        promptField,
                        valuesByFieldId[promptField.id]
                      ) === 'string'
                        ? String(
                            getDisplayValue(
                              promptField,
                              valuesByFieldId[promptField.id]
                            )
                          )
                        : ''
                    }
                    minLength={promptField.minLength}
                    maxLength={promptField.maxLength}
                    rows={3}
                    onChange={(event) =>
                      handleValueChange(
                        promptField.id,
                        event.currentTarget.value
                      )
                    }
                  />
                  {promptField.infoText ? (
                    <span
                      id={`${formId}-${promptField.id}-hint`}
                      className="sr-only"
                    >
                      {promptField.infoText}
                    </span>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>

          <div className="cms-block-hero-hero-2-hero-2__controls">
            <div className="cms-block-hero-hero-2-hero-2__advancedField">
              <button
                type="button"
                className="cms-block-hero-hero-2-hero-2__modelControl"
                data-hero-2-model-trigger
                aria-label={`Model: ${prefillForm.toolLabel}`}
                aria-expanded={openSurface === 'model'}
                onClick={() =>
                  setOpenSurface((current) =>
                    current === 'model' ? null : 'model'
                  )
                }
              >
                <span className="cms-block-hero-hero-2-hero-2__triggerValue">
                  {prefillForm.toolLabel}
                </span>
                {renderAppIcon({
                  name: 'chevron-down',
                  className:
                    openSurface === 'model'
                      ? 'cms-block-hero-hero-2-hero-2__triggerChevron cms-block-hero-hero-2-hero-2__triggerChevronOpen'
                      : 'cms-block-hero-hero-2-hero-2__triggerChevron',
                })}
              </button>
              {openSurface === 'model' && isMobileViewport ? (
                <WorkspaceMobileDrawer
                  id={`workspace-hero-2-${prefillForm.toolId}-model-drawer`}
                  open
                  onOpenChange={(open) => {
                    if (!open) setOpenSurface(null);
                  }}
                  title="Model"
                >
                  <button
                    type="button"
                    className="cms-block-hero-hero-2-hero-2__modelOption"
                    aria-pressed="true"
                    onClick={() => setOpenSurface(null)}
                  >
                    <span>{prefillForm.toolLabel}</span>
                  </button>
                </WorkspaceMobileDrawer>
              ) : openSurface === 'model' ? (
                <div
                  className="cms-block-hero-hero-2-hero-2__advancedSurface"
                  data-surface="model"
                  onClick={() => setOpenSurface(null)}
                >
                  <dialog
                    open
                    className="cms-block-hero-hero-2-hero-2__advancedPanel cms-block-hero-hero-2-hero-2__modelPanel"
                    aria-label={`Current model: ${prefillForm.toolLabel}`}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="cms-block-hero-hero-2-hero-2__modelOption"
                      aria-pressed="true"
                      onClick={() => setOpenSurface(null)}
                    >
                      <span>{prefillForm.toolLabel}</span>
                    </button>
                  </dialog>
                </div>
              ) : null}
            </div>

            {parameterFields.length > 0 ? (
              <div className="cms-block-hero-hero-2-hero-2__advancedField">
                <button
                  type="button"
                  className="cms-block-hero-hero-2-hero-2__parameterSummaryButton"
                  data-hero-2-parameters-trigger
                  aria-label={parameterFields
                    .map((field) => field.label)
                    .join(', ')}
                  aria-expanded={openSurface === 'parameters'}
                  onClick={() => {
                    setOpenSurface((current) =>
                      current === 'parameters' ? null : 'parameters'
                    );
                  }}
                >
                  {parameterSummaryItems.map((item, index) => (
                    <span
                      key={item.id}
                      className="cms-block-hero-hero-2-hero-2__parameterSummaryGroup"
                    >
                      {index > 0 ? (
                        <span className="cms-block-hero-hero-2-hero-2__parameterSummaryDivider" />
                      ) : null}
                      <span className="cms-block-hero-hero-2-hero-2__parameterSummaryItem">
                        {item.label}
                      </span>
                    </span>
                  ))}
                  {renderAppIcon({
                    name: 'chevron-down',
                    className:
                      openSurface === 'parameters'
                        ? 'cms-block-hero-hero-2-hero-2__triggerChevron cms-block-hero-hero-2-hero-2__triggerChevronOpen'
                        : 'cms-block-hero-hero-2-hero-2__triggerChevron',
                  })}
                </button>

                {openSurface === 'parameters' && isMobileViewport ? (
                  <WorkspaceMobileDrawer
                    id={`workspace-hero-2-${prefillForm.toolId}-parameters-drawer`}
                    open
                    onOpenChange={(open) => {
                      if (!open) setOpenSurface(null);
                    }}
                    title="Parameters"
                  >
                    <WorkspaceParamControls
                      fields={parameterFields}
                      values={parameterValuesByFieldId}
                      premiumFeatureTooltip={premiumFeatureTooltip}
                      showPaidFeatureMarkers={showPaidFeatureMarkers}
                      onChange={handleValueChange}
                    />
                  </WorkspaceMobileDrawer>
                ) : openSurface === 'parameters' ? (
                  <div
                    className="cms-block-hero-hero-2-hero-2__advancedSurface"
                    data-surface="parameters"
                    onClick={() => setOpenSurface(null)}
                  >
                    <dialog
                      open
                      className="cms-block-hero-hero-2-hero-2__advancedPanel"
                      aria-label={parameterFields
                        .map((field) => field.label)
                        .join(', ')}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <WorkspaceParamControls
                        fields={parameterFields}
                        values={parameterValuesByFieldId}
                        premiumFeatureTooltip={premiumFeatureTooltip}
                        showPaidFeatureMarkers={showPaidFeatureMarkers}
                        onChange={handleValueChange}
                      />
                    </dialog>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="cms-block-hero-hero-2-hero-2__startButtonWrap">
              <Button
                type="button"
                size="lg"
                className="cms-block-hero-hero-2-hero-2__startButton"
                data-hero-2-submit
                onClick={handleSubmit}
              >
                <span className="cms-block-hero-hero-2-hero-2__startButtonLabel">
                  {buttonBinding.button?.label ?? primaryButtonLabel}
                </span>
                <span className="cms-block-hero-hero-2-hero-2__startButtonCredits">
                  {renderAppIcon({
                    name: 'Coins',
                    className:
                      'cms-block-hero-hero-2-hero-2__startButtonCreditIcon',
                  })}
                  <span>{requiredCredits ?? '—'}</span>
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
